import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../../app/store";
import { redirectToSamlLogin, SAML_CALLBACK_PATH } from "../../auth/samlAuth";
import { useLoginMutation } from "../../features/auth/auth.api";
import { setCredentials } from "../../features/auth/auth.slice";
import { AuthProvider } from "../../features/auth/auth.types";
import {
	extractAuthErrorMessage,
	hasAuthCallbackParams,
} from "../../features/auth/auth.utils";
import { FORCE_LOGOUT_MESSAGE_KEY } from "../../features/auth/authApiErrors";
import { getDefaultAuthenticatedPath } from "../../features/auth/defaultAuthenticatedPath";
import { addToast } from "../../features/toast/toast.slice";
import { LoginBrandPanel } from "./components/LoginBrandPanel";
import { LoginForm } from "./components/LoginForm";
import type { LoginFormValues } from "./loginForm.utils";

export const Login = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [login] = useLoginMutation();
	const isAuthenticated = useSelector(
		(state: RootState) => state.auth.isAuthenticated,
	);
	const user = useSelector((state: RootState) => state.auth.user);
	const [authError, setAuthError] = useState<string | null>(null);
	const [isCredentialsSubmitting, setIsCredentialsSubmitting] = useState(false);
	const [isSsoRedirecting, setIsSsoRedirecting] = useState(false);

	useEffect(() => {
		if (isAuthenticated) {
			navigate(getDefaultAuthenticatedPath(user), { replace: true });
		}
	}, [isAuthenticated, navigate, user]);

	// Show API message after force-logout hard redirect (sessionStorage).
	useEffect(() => {
		try {
			const message = sessionStorage.getItem(FORCE_LOGOUT_MESSAGE_KEY);
			if (!message) return;
			sessionStorage.removeItem(FORCE_LOGOUT_MESSAGE_KEY);
			dispatch(addToast({ type: "error", message }));
		} catch {
			// ignore storage errors
		}
	}, [dispatch]);

	useEffect(() => {
		if (hasAuthCallbackParams(window.location)) {
			const suffix = `${window.location.search}${window.location.hash}`;
			navigate(`${SAML_CALLBACK_PATH}${suffix}`, { replace: true });
		}
	}, [navigate]);

	const handleCredentialsLogin = async (values: LoginFormValues) => {
		setIsCredentialsSubmitting(true);
		setAuthError(null);

		try {
			const session = await login({
				email: values.email,
				password: values.password,
			}).unwrap();

			if (!session.accessToken || !session.user.id) {
				throw new Error("Sign-in succeeded, but session data is incomplete.");
			}

			dispatch(
				setCredentials({
					accessToken: session.accessToken,
					refreshToken: session.refreshToken,
					user: session.user,
					authProvider: AuthProvider.CREDENTIALS,
				}),
			);
			navigate(getDefaultAuthenticatedPath(session.user), { replace: true });
		} catch (err) {
			setAuthError(extractAuthErrorMessage(err));
		} finally {
			setIsCredentialsSubmitting(false);
		}
	};

	const handleSsoLogin = () => {
		setIsSsoRedirecting(true);
		setAuthError(null);
		try {
			redirectToSamlLogin();
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: "Unable to start sign-in. Please try again.";
			setAuthError(message);
			setIsSsoRedirecting(false);
		}
	};

	return (
		<div className="flex min-h-full w-full bg-bg-page">
			<LoginBrandPanel />

			<div className="relative flex min-h-full flex-1 flex-col overflow-y-auto">
				<div
					className="pointer-events-none absolute inset-0 lg:hidden"
					aria-hidden
					style={{
						background:
							"linear-gradient(180deg, rgba(163,21,21,0.08) 0%, transparent 28%), radial-gradient(circle at 100% 0%, rgba(163,21,21,0.06) 0%, transparent 40%)",
					}}
				/>

				<div className="relative flex flex-1 flex-col justify-center px-6 py-10 sm:px-10 lg:px-16 xl:px-24">
					<div className="mb-8 lg:hidden">
						<p className="text-xl font-bold tracking-tight text-primary">
							Private Homes
						</p>
						<p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-muted">
							Admin Portal
						</p>
					</div>

					<LoginForm
						onCredentialsLogin={handleCredentialsLogin}
						onSsoLogin={handleSsoLogin}
						authError={authError}
						isCredentialsSubmitting={isCredentialsSubmitting}
						isSsoRedirecting={isSsoRedirecting}
					/>
				</div>

				<footer className="relative shrink-0 px-6 py-5 text-center text-[11px] text-primary-muted sm:px-10 lg:text-left lg:px-16 xl:px-24">
					© {new Date().getFullYear()} Kerzner International. Internal use only.
				</footer>
			</div>
		</div>
	);
};
